/**
 * 任意テキスト管理画面 (独自テンプレート + 配布テンプレートを 1 画面に統合)。
 *
 * 放送地点マスタの任意テキスト放送で使う定型の文面を、**線区別** に保守する GUI。
 * ControlPanel API の `/free-text-templates` (独自テンプレート / コピー型) と
 * `/synced-texts` (配布テンプレート / 参照型) を利用する。
 * DynamoDB は 2 テーブル (FreeTextTemplateMaster / SyncedTextMaster) のままで、画面・権限キー・通知種別だけを
 * 「任意テキスト」1 機能にそろえている。画面構成と操作感は SSML 辞書マスタ画面 (`SsmlDictionaryScreen`) に合わせる。
 *
 * 画面構成:
 *   - 上 (固定): 操作バー (線区の絞り込み + 「＋ 新規作成」は**画面に 1 つだけ** + 再読み込み)。
 *   - 中 (固定): 見出しと説明文。
 *   - 下 (スクロール): エラーバナー + 2 つの表 (「独自テンプレート」「配布テンプレート」)。
 *         列は 線区 / タイトル / テキスト / 操作。備考はタイトルの下に小さく表示し、http(s) の URL をリンクにする。
 *         並びは 全線区共通 → 線区マスタの並び → テキストのあいうえお順。
 *         行の「編集」でモーダルを開き、「削除」は確認ダイアログ (`ConfirmDialog`) を経る。
 *   - 「＋ 新規作成」のモーダルは種別ラジオ (独自 / 配布、既定は独自) で保存先を選ぶ。編集中は種別を変えられない。
 *
 * 線区の絞り込み (2 つの表とも同じ条件で絞る):
 *   - 「(すべての線区)」: 絞り込まない (全件)。
 *   - 「全線区共通」: 全線区共通 (`__all__`) のみ。
 *   - 各線区: その線区のテキストのみ (全線区共通のテキストは出さない。共通分は「全線区共通」で確認する)。
 *     ※ 放送地点マスタの選択肢 (`FreeTextTemplatePicker`) は別仕様で、全線区共通も候補に含める。
 *   選択は URL クエリ `?route=京浜` で保持する (リロード / URL 共有で復元される)。旧共有テキスト画面の
 *   `/synced-texts?route=...` はこの画面へリダイレクトされる (`App.tsx`)。
 *   `useRouteSelection` は使わない: あれはトップページの線区・方面の選択状態を
 *   sessionStorage に保存するため、この画面の絞り込みで上書きしてしまうのを避ける。
 *
 * 保存・削除の完了は `NoticeDialog` のポップアップで知らせる。保存時にサーバが返す
 * `warnings` (同じ線区に同じ本文が登録済み) は同じポップアップに併記する。
 * エラー (サーバの英語メッセージは日本語に変換) は背景 + 枠付きのバナーで出す。
 *
 * ⚠️ 2 種類のテンプレートで、編集・削除の影響が違う:
 *   - 独自テンプレート: 選択時に本文がコピーされるだけ。編集・削除しても登録済みの放送地点は変わらない。
 *   - 配布テンプレート: 放送地点が「参照」する。編集は参照先の放送に約 1 分以内に反映され、削除は参照中ならサーバが
 *     409 で拒否して参照元の放送地点を一覧で示す (参照切れで放送が静かに止まるのを防ぐ)。
 *
 * 試聴:
 *   - 各行の「▶ 試聴」で、その行の本文を聴ける (SSML 辞書マスタ画面と同じ操作感)。
 *     押すたびに `POST /audio-preview` で合成する (キャッシュしない)。
 *   - 本番の放送生成と同じく SSML 辞書を適用して合成する (聴いた音 = 本番の音)。
 *   - 新規 / 編集モーダルにも、保存前の本文を聴ける「▶ 試聴」がある。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';
import { useSearchParams } from 'react-router';

import { ApiError } from '../api/common';
import { FreeTextTemplatesClient } from '../api/freeTextTemplates';
import { SyncedTextsClient } from '../api/syncedTexts';
import type {
  FreeTextTemplate,
  FreeTextTemplateSaveResult,
  SyncedText,
  SyncedTextInUseBody,
} from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import type { AppConfig } from '../config';
import { BroadcastPointsClient, base64ToAudioUrl } from '../api/broadcastPoints';
import {
  FREE_TEXT_TEMPLATE_ALL_ROUTES,
  FREE_TEXT_TEMPLATE_ALL_ROUTES_LABEL,
  freeTextTemplateRouteLabel,
  sortFreeTextTemplates,
  TEMPLATE_TITLE_UNSET_LABEL,
} from '../data/freeTextTemplates';
import { fetchRoutesMaster, type RouteMaster } from '../data/routes';
import { useHistoryWarning } from '../hooks/useHistoryWarning';
import { ROUTE_QUERY_KEY } from '../hooks/useRouteSelection';
import { freeTextTemplateTarget } from '../pages/masterHistoryTarget';

import { ConfirmDialog } from './ConfirmDialog';
import { FreeTextTemplateFormModal, type FreeTextKind } from './FreeTextTemplateFormModal';
import { buildSavedNoticeMessage, describeApiError } from './freeTextTemplateMessages';
import { MasterHistoryLink } from './MasterHistoryLink';
import { NoticeDialog } from './NoticeDialog';
import { LinkifiedText } from './LinkifiedText';
import { StatusBanner } from './StatusBanner';

export interface FreeTextTemplateScreenProps {
  config: AppConfig;
}

/** 絞り込みなし (全件) を表すセレクトの値 (URL クエリでは `route` を付けない) */
const FILTER_ALL = '';

/** 画面が扱う 1 行 (独自テンプレートと配布テンプレートは同じ形) */
type TextRow = FreeTextTemplate | SyncedText;

type ModalState =
  | { mode: 'create' }
  | { mode: 'edit'; kind: FreeTextKind; template: TextRow }
  | null;

/** 削除確認の対象 (種別ごとに削除先 API と文言が違う) */
interface DeleteTarget {
  kind: FreeTextKind;
  template: TextRow;
}

/** 種別ごとの表示名 */
const KIND_LABELS: Readonly<Record<FreeTextKind, string>> = {
  free: '独自テンプレート',
  synced: '配布テンプレート',
};

/** 行を一意に表すキー (`(route_id, template_id)` がテンプレートのキー。種別をまたいで衝突させない) */
function templateKey(kind: FreeTextKind, template: TextRow): string {
  return `${kind}:${template.route_id}/${template.template_id}`;
}

/** 削除確認ダイアログに出す、テキストの要約 (長い本文は省略する) */
function summarize(text: string): string {
  const singleLine = text.replace(/\s*[\r\n]+\s*/g, ' ').trim();
  const chars = Array.from(singleLine);
  return chars.length <= 40 ? singleLine : `${chars.slice(0, 40).join('')}…`;
}

/** 参照元を一覧表示する件数の上限 (サーバは最大 50 件返すが、バナーが長くならないように絞る) */
const MAX_IN_USE_REFERENCES_SHOWN = 10;

/**
 * 配布テンプレートの削除拒否 (409) のメッセージを作る。参照している放送地点を、線区#方面 / 説明 で列挙する。
 * 本文のボディが想定外の形でも壊れないよう、型を確かめてから読む。
 */
function describeInUseError(err: ApiError): string {
  const body = err.body as Partial<SyncedTextInUseBody> | undefined;
  const references = Array.isArray(body?.references) ? body.references : [];
  const total =
    typeof body?.reference_count === 'number' ? body.reference_count : references.length;
  const shown = references.slice(0, MAX_IN_USE_REFERENCES_SHOWN).map((ref) => {
    const description = ref.description ? ` / ${ref.description}` : '';
    return `・${ref.routeDirection ?? '(不明)'}${description}（コンテンツ ${ref.content_index + 1}）`;
  });
  const rest = total - shown.length;
  const lines = [
    `この配布テンプレートは ${total} 件の放送地点が参照しているため削除できません。`,
    '参照を外して(別のテキストに変える / コンテンツを削除する)から、もう一度削除してください。',
    ...shown,
  ];
  if (rest > 0) lines.push(`・ほか ${rest} 件`);
  return lines.join('\n');
}

/** 絞り込み後の一覧 (全線区共通 → 線区マスタの並び → テキストのあいうえお順) */
function filterAndSort(
  items: readonly TextRow[],
  filterRouteId: string,
  routes: readonly RouteMaster[],
): TextRow[] {
  const filtered = items.filter((t) => {
    if (filterRouteId === FILTER_ALL) return true;
    // 選んだ絞り込み値 (線区 / 「全線区共通」) と route_id が一致するものだけを出す。
    // 線区選択中に全線区共通のテキストが混ざらないようにする。
    return t.route_id === filterRouteId;
  });
  return sortFreeTextTemplates(filtered, routes);
}

export function FreeTextTemplateScreen(props: FreeTextTemplateScreenProps): React.JSX.Element {
  const { config } = props;
  const { getIdToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  /** 保存後の「変更履歴の登録に失敗」警告 (マスタ変更履歴 SBI 8)。× で閉じるまで消えない */
  const { historyWarning, reportHistory } = useHistoryWarning('free-text-template-history-warning');
  const filterRouteId = searchParams.get(ROUTE_QUERY_KEY) ?? FILTER_ALL;

  const apiClient = useMemo(
    () => new FreeTextTemplatesClient({ getIdToken, config }),
    [getIdToken, config],
  );
  const syncedClient = useMemo(
    () => new SyncedTextsClient({ getIdToken, config }),
    [getIdToken, config],
  );
  /** 試聴用 (`POST /audio-preview`)。放送地点 API のクライアントを流用する */
  const previewClient = useMemo(
    () => new BroadcastPointsClient({ getIdToken, config }),
    [getIdToken, config],
  );

  const [routes, setRoutes] = useState<RouteMaster[]>([]);
  const [templates, setTemplates] = useState<FreeTextTemplate[]>([]);
  const [syncedTexts, setSyncedTexts] = useState<SyncedText[]>([]);
  const [modal, setModal] = useState<ModalState>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  /** 削除確認ダイアログの対象 */
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleting, setDeleting] = useState(false);
  /** 保存 / 削除の完了を知らせるポップアップの本文 (null = 表示しない)。保存では警告も含む */
  const [noticeMessage, setNoticeMessage] = useState<string | null>(null);
  /** 試聴の音声を生成中の行のキー (null = 生成中なし) */
  const [previewingKey, setPreviewingKey] = useState<string | null>(null);
  /** 再生中の音声 (新しく再生するとき / 画面を離れるときに止めて URL を解放する) */
  const playingRef = useRef<{ audio: HTMLAudioElement; url: string } | null>(null);
  /** 一覧取得の世代カウンタ (古いレスポンスで新しい結果を上書きしない防御) */
  const requestIdRef = useRef(0);

  /**
   * 両方の一覧を取得して state に反映する。
   * 片方だけ失敗しても、取得できた側は表示する (権限が片方だけ…ではなく通信の一時失敗に備える)。
   * 失敗した側があればエラーバナーを出す。
   */
  const reload = useCallback(async (): Promise<void> => {
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setErrorMessage(null);
    try {
      const [freeResult, syncedResult] = await Promise.allSettled([
        apiClient.list(),
        syncedClient.list(),
      ]);
      if (currentRequestId !== requestIdRef.current) return;
      if (freeResult.status === 'fulfilled') setTemplates(freeResult.value.items);
      if (syncedResult.status === 'fulfilled') setSyncedTexts(syncedResult.value.items);
      const failure =
        freeResult.status === 'rejected'
          ? freeResult.reason
          : syncedResult.status === 'rejected'
            ? syncedResult.reason
            : null;
      if (failure !== null) setErrorMessage(describeApiError(failure).message);
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [apiClient, syncedClient]);

  useEffect(() => {
    void reload();
    return () => {
      // アンマウント時に世代を進め、進行中リクエストの結果を破棄する。
      // eslint-disable-next-line react-hooks/exhaustive-deps -- ref は DOM ではなくリクエスト世代カウンタ
      ++requestIdRef.current;
    };
  }, [reload]);

  /** 線区マスタ (絞り込み / 作成モーダルの選択肢と表示名)。失敗しても線区 ID 表示で操作は続けられる。 */
  useEffect(() => {
    let aborted = false;
    void (async () => {
      try {
        const rm = await fetchRoutesMaster();
        if (!aborted) setRoutes(rm.routes);
      } catch {
        // 線区名が表示できないだけなので無視する (一覧の取得は妨げない)
      }
    })();
    return () => {
      aborted = true;
    };
  }, []);

  const visibleTemplates = useMemo(
    () => filterAndSort(templates, filterRouteId, routes),
    [templates, filterRouteId, routes],
  );
  const visibleSyncedTexts = useMemo(
    () => filterAndSort(syncedTexts, filterRouteId, routes),
    [syncedTexts, filterRouteId, routes],
  );

  const handleFilterChange = useCallback(
    (routeId: string): void => {
      const next = new URLSearchParams(searchParams);
      if (routeId === FILTER_ALL) next.delete(ROUTE_QUERY_KEY);
      else next.set(ROUTE_QUERY_KEY, routeId);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  /** 直前のエラー / 完了通知の表示を消す (次の操作に古い表示を持ち越さない)。 */
  const clearMessages = useCallback((): void => {
    setErrorMessage(null);
    setNoticeMessage(null);
  }, []);

  const handleCreate = useCallback((): void => {
    clearMessages();
    setModal({ mode: 'create' });
  }, [clearMessages]);

  const handleEdit = useCallback(
    (kind: FreeTextKind, template: TextRow): void => {
      clearMessages();
      setModal({ mode: 'edit', kind, template });
    },
    [clearMessages],
  );

  /** モーダルでの保存成功: 完了ポップアップ (警告があれば併記) を出し、モーダルを閉じて再取得する。 */
  const handleSaved = useCallback(
    (result: FreeTextTemplateSaveResult, kind: FreeTextKind): void => {
      const action = modal?.mode === 'edit' ? '更新' : '登録';
      setNoticeMessage(buildSavedNoticeMessage(action, result.warnings ?? [], KIND_LABELS[kind]));
      // モーダルはここで閉じるので、履歴の警告はモーダルではなく画面側に出す
      reportHistory(result);
      setModal(null);
      void reload();
    },
    [modal, reload, reportHistory],
  );

  /** 再生中の音声を止めて、object URL を解放する。 */
  const stopPlaying = useCallback((): void => {
    const playing = playingRef.current;
    if (!playing) return;
    playing.audio.pause();
    URL.revokeObjectURL(playing.url);
    playingRef.current = null;
  }, []);

  useEffect(() => stopPlaying, [stopPlaying]);

  /**
   * 行の「▶ 試聴」。その行の本文を合成して再生する (押すたびに合成し、キャッシュしない)。
   * 辞書は適用する (本番の放送生成と同じ)。言語・話者は指定しない (日本語・サーバ既定話者)。
   */
  const handlePreview = useCallback(
    async (kind: FreeTextKind, template: TextRow): Promise<void> => {
      const text = template.text.trim();
      if (text === '') return;
      stopPlaying();
      clearMessages();
      setPreviewingKey(templateKey(kind, template));
      try {
        const res = await previewClient.previewAudio({ text, isSsml: false });
        const url = base64ToAudioUrl(res.content_base64, res.content_type);
        const audio = new Audio(url);
        playingRef.current = { audio, url };
        await audio.play();
      } catch (err) {
        stopPlaying();
        setErrorMessage(describeApiError(err).message);
      } finally {
        setPreviewingKey(null);
      }
    },
    [clearMessages, previewClient, stopPlaying],
  );

  /** 削除確認ダイアログで確定されたときの削除処理。 */
  const handleConfirmDelete = useCallback(async (): Promise<void> => {
    if (!deleteTarget) return;
    const { kind, template } = deleteTarget;
    setDeleting(true);
    clearMessages();
    try {
      const client = kind === 'synced' ? syncedClient : apiClient;
      reportHistory(await client.remove(template.route_id, template.template_id));
      await reload();
      setNoticeMessage(`${KIND_LABELS[kind]}を削除しました`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // 先に他者が削除済み: 削除したい状態にはなっているのでメッセージは出さず、一覧だけ更新する (Issue #765)。
        await reload();
      } else if (kind === 'synced' && err instanceof ApiError && err.status === 409) {
        // 放送地点が参照中: 削除せず、参照元を示して外してから削除するよう促す。
        setErrorMessage(describeInUseError(err));
      } else {
        setErrorMessage(describeApiError(err).message);
      }
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }, [apiClient, clearMessages, deleteTarget, reload, reportHistory, syncedClient]);

  /** 編集中に他者が削除していた: お知らせを閉じた後、モーダルを閉じて一覧を取り直す (Issue #765)。 */
  const handleDeletedByOthers = useCallback((): void => {
    setModal(null);
    void reload();
  }, [reload]);

  /** 新規作成モーダルで最初に選ぶ線区 (絞り込み中の線区。絞り込みなしは全線区共通) */
  const createDefaultRouteId =
    filterRouteId === FILTER_ALL ? FREE_TEXT_TEMPLATE_ALL_ROUTES : filterRouteId;

  /** 1 種別ぶんの表 (独自テンプレート / 配布テンプレートで列・操作・testid の接頭辞だけが違う)。 */
  const renderTable = (
    kind: FreeTextKind,
    rows: readonly TextRow[],
    testIdPrefix: string,
  ): React.JSX.Element => (
    <table className="points-table free-text-template-table" data-testid={`${testIdPrefix}-table`}>
      <colgroup>
        <col className="free-text-template-col-route" />
        <col className="free-text-template-col-title" />
        <col className="free-text-template-col-text" />
        <col className="free-text-template-col-actions" />
      </colgroup>
      <thead>
        <tr>
          <th>線区</th>
          <th>タイトル</th>
          <th>テキスト</th>
          <th>操作</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && !loading ? (
          <tr>
            <td colSpan={4} className="empty-state" data-testid={`${testIdPrefix}-empty`}>
              登録された{KIND_LABELS[kind]}はありません
            </td>
          </tr>
        ) : null}
        {rows.map((template) => (
          <tr
            key={templateKey(kind, template)}
            data-testid={`${testIdPrefix}-row-${template.template_id}`}
          >
            <td>{freeTextTemplateRouteLabel(template.route_id, routes)}</td>
            <td
              className="free-text-template-title-cell"
              data-testid={`${testIdPrefix}-title-${template.template_id}`}
            >
              {template.title ? (
                <strong>{template.title}</strong>
              ) : (
                <span className="free-text-template-title-unset">{TEMPLATE_TITLE_UNSET_LABEL}</span>
              )}
              {template.note ? (
                <LinkifiedText
                  text={template.note}
                  className="free-text-template-note"
                  data-testid={`${testIdPrefix}-note-${template.template_id}`}
                />
              ) : null}
            </td>
            <td className="free-text-template-text-cell">{template.text}</td>
            <td className="row-actions">
              <button
                type="button"
                onClick={() => void handlePreview(kind, template)}
                disabled={deleting || previewingKey !== null}
                data-testid={`${testIdPrefix}-preview-${template.template_id}`}
              >
                {previewingKey === templateKey(kind, template) ? '生成中...' : '▶ 試聴'}
              </button>
              <button
                type="button"
                onClick={() => handleEdit(kind, template)}
                disabled={deleting}
                data-testid={`${testIdPrefix}-edit-${template.template_id}`}
              >
                編集
              </button>
              <button
                type="button"
                className="danger"
                onClick={() => setDeleteTarget({ kind, template })}
                disabled={deleting}
                data-testid={`${testIdPrefix}-delete-${template.template_id}`}
              >
                削除
              </button>
              {/* 配布テンプレートも独自テンプレートと同じ履歴種別 (free_text_template) に記録される。template_id は UUID で衝突しない */}
              <MasterHistoryLink
                target={freeTextTemplateTarget(template.route_id, template.template_id)}
                subject={`${freeTextTemplateRouteLabel(template.route_id, routes)}の${kind === 'synced' ? '配布' : '独自'}テンプレート`}
                testId={`${testIdPrefix}-history-${template.template_id}`}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="free-text-template-page">
      <section
        className="section"
        aria-label="任意テキストテンプレート"
        data-testid="free-text-template-screen"
      >
        {/* 操作バー: 放送地点マスタの `RouteSelector` と同じ並び (線区 + 新規作成 + 再読み込み)。固定表示 */}
        <div className="route-selector" data-testid="free-text-template-toolbar">
          <div className="route-selector__selects">
            <label>
              <span>線区</span>
              <select
                data-testid="free-text-template-filter"
                aria-label="線区"
                value={filterRouteId}
                onChange={(e) => handleFilterChange(e.target.value)}
              >
                <option value={FILTER_ALL}>(すべての線区)</option>
                <option value={FREE_TEXT_TEMPLATE_ALL_ROUTES}>
                  {FREE_TEXT_TEMPLATE_ALL_ROUTES_LABEL}
                </option>
                {routes.map((r) => (
                  <option key={r.route_id} value={r.route_id}>
                    {r.route_name} ({r.route_id})
                  </option>
                ))}
              </select>
            </label>
          </div>
          <button
            type="button"
            className="primary create-button"
            data-testid="free-text-template-create"
            onClick={handleCreate}
          >
            ＋ 新規作成
          </button>
          <div className="actions">
            <button
              type="button"
              data-testid="free-text-template-reload"
              onClick={() => void reload()}
              disabled={loading}
            >
              再読み込み
            </button>
          </div>
        </div>
      </section>

      <section
        className="section free-text-template-list-section"
        data-testid="free-text-template-list-section"
      >
        {/* 見出し行 (スクロールしない): 見出しと説明文を固定し、本文だけをスクロールさせる */}
        <header
          className="free-text-template-list-header"
          data-testid="free-text-template-list-header"
        >
          <h2>テンプレート一覧</h2>
          {historyWarning}
          <p className="free-text-template-lead">
            駅などの放送地点で流す「任意テキスト」のテンプレートを、<strong>線区ごと</strong>
            に登録しておく画面です。テンプレートは 2 種類あります。文面を<strong>コピー</strong>
            して自由に直して使うのが<strong>独自テンプレート</strong>、同一の文面を
            <strong>みんなで</strong>使うのが<strong>配布テンプレート</strong>です。
          </p>
        </header>

        <div className="free-text-template-list-body" data-testid="free-text-template-list-body">
          {errorMessage ? (
            <StatusBanner
              variant="error"
              onDismiss={() => setErrorMessage(null)}
              data-testid="free-text-template-error"
            >
              {/* 参照中の削除拒否は参照元を改行区切りで列挙するため、改行を保つ */}
              <span style={{ whiteSpace: 'pre-line' }}>{errorMessage}</span>
            </StatusBanner>
          ) : null}

          <div className="free-text-template-group" data-testid="free-text-template-group-free">
            <h3>独自テンプレート</h3>
            <p className="free-text-template-group-note">
              放送地点に使うときは、文面がコピーされます。そのため、ここで直したり消したりしても、すでに登録した放送地点の文面は変わりません。
            </p>
            {renderTable('free', visibleTemplates, 'free-text-template')}
          </div>

          <div className="free-text-template-group" data-testid="free-text-template-group-synced">
            <h3>配布テンプレート</h3>
            <p className="free-text-template-group-note">
              放送地点は文面をコピーせず、ここの文面を参照します。ここを直すと、使っているすべての放送地点の放送が約
              1 分以内に自動で変わります。いずれかの放送地点で使っている間は削除できません。
            </p>
            {renderTable('synced', visibleSyncedTexts, 'synced-text')}
          </div>
        </div>
      </section>

      {modal ? (
        <FreeTextTemplateFormModal
          mode={modal.mode}
          template={modal.mode === 'edit' ? modal.template : null}
          initialKind={modal.mode === 'edit' ? modal.kind : 'free'}
          defaultRouteId={createDefaultRouteId}
          routes={routes}
          apiClient={apiClient}
          syncedClient={syncedClient}
          previewClient={previewClient}
          onSaved={handleSaved}
          onDeletedByOthers={handleDeletedByOthers}
          onClose={() => setModal(null)}
        />
      ) : null}

      <ConfirmDialog
        open={deleteTarget !== null}
        title={deleteTarget ? `${KIND_LABELS[deleteTarget.kind]}の削除` : 'テンプレートの削除'}
        message={
          deleteTarget
            ? `次の${KIND_LABELS[deleteTarget.kind]}を削除します。よろしいですか？\n(${freeTextTemplateRouteLabel(deleteTarget.template.route_id, routes)})\n${
                deleteTarget.template.title ? `タイトル: ${deleteTarget.template.title}\n` : ''
              }「${summarize(deleteTarget.template.text)}」\n${
                deleteTarget.kind === 'synced'
                  ? 'このテキストを参照している放送地点がある場合は削除できません。'
                  : '登録済みの放送地点の文面は変わりません。'
              }`
            : ''
        }
        confirmLabel="削除"
        destructive
        confirmDisabled={deleting}
        cancelDisabled={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* 保存 / 削除の完了は、画面に埋め込まずポップアップで知らせる */}
      <NoticeDialog
        open={noticeMessage !== null}
        message={noticeMessage ?? ''}
        onClose={() => setNoticeMessage(null)}
      />
    </div>
  );
}
